import { useState } from 'react';
import { Icons } from '../Icons';
import { PINPad } from './PINPad';
import { pinService } from '../../services/pinService';
import { biometricAuth } from '../../services/biometricAuth';
import { analytics } from '../../services/analytics';
import './LockScreen.css';

interface LockScreenProps {
  onUnlock: () => void;
}

type AuthMethod = 'pin' | 'biometric';

export function LockScreen({ onUnlock }: LockScreenProps) {
  const [authMethod, setAuthMethod] = useState<AuthMethod>('pin');
  const [error, setError] = useState<string | null>(null);
  const [isAuthenticating, setIsAuthenticating] = useState(false);

  const isBiometricAvailable = biometricAuth.isBiometricSupported() && biometricAuth.isBiometricEnabled();

  const handlePINSubmit = async (pin: string) => {
    console.log('🔢 PIN submitted for verification');
    setIsAuthenticating(true);
    setError(null);

    try {
      const isValid = await pinService.verifyPIN(pin);

      if (isValid) {
        console.log('✅ PIN verified, unlocking app');
        analytics.event('pin_unlock_success');
        onUnlock();
      } else {
        console.log('❌ Invalid PIN');
        analytics.event('pin_unlock_failed');
        setError('Code PIN incorrect');
      }
    } catch (err) {
      console.error('❌ Error verifying PIN:', err);
      setError('Erreur lors de la vérification');
    } finally {
      setIsAuthenticating(false);
    }
  };

  const handleBiometricAuth = async () => {
    console.log('🔐 Biometric authentication requested');
    setIsAuthenticating(true);
    setError(null);

    try {
      const success = await biometricAuth.authenticate();
      if (success) {
        console.log('✅ Biometric authentication successful');
        analytics.event('biometric_unlock_success');
        onUnlock();
      } else {
        console.log('❌ Biometric authentication failed');
        analytics.event('biometric_unlock_failed');
        setError('Échec de l\'authentification. Utilisez votre PIN.');
        // Switch back to PIN
        setAuthMethod('pin');
      }
    } catch (err) {
      console.error('❌ Biometric authentication error:', err);
      analytics.event('biometric_unlock_error');
      setError('Erreur d\'authentification. Utilisez votre PIN.');
      // Switch back to PIN
      setAuthMethod('pin');
    } finally {
      setIsAuthenticating(false);
    }
  };

  return (
    <div className="lock-screen">
      <div className="lock-screen-content">
        {/* Logo/Icon */}
        <div className="lock-screen-icon">
          <Icons.Lock size={80} />
        </div>

        {/* Title */}
        <h1 className="lock-screen-title">Portfolio Verrouillé</h1>
        <p className="lock-screen-description">
          Entrez votre code PIN pour déverrouiller
        </p>

        {/* PIN Entry */}
        {authMethod === 'pin' && (
          <div className="lock-auth-container">
            <PINPad
              length={6}
              onComplete={handlePINSubmit}
              title=""
              error={error}
              loading={isAuthenticating}
            />
          </div>
        )}

        {/* Biometric Auth */}
        {authMethod === 'biometric' && (
          <div className="lock-auth-container biometric">
            <div className="biometric-prompt">
              <Icons.Fingerprint size={64} />
              <p className="biometric-hint">Touchez le capteur</p>
            </div>

            {error && (
              <div className="lock-error">
                <Icons.AlertCircle size={20} />
                {error}
              </div>
            )}

            <button
              className="unlock-btn primary"
              onClick={handleBiometricAuth}
              disabled={isAuthenticating}
            >
              {isAuthenticating ? (
                <>
                  <Icons.Loader size={24} className="spinner" />
                  Authentification...
                </>
              ) : (
                <>
                  <Icons.Fingerprint size={24} />
                  Authentifier
                </>
              )}
            </button>
          </div>
        )}

        {/* Auth Method Switcher */}
        <div className="auth-switcher">
          {authMethod === 'pin' && isBiometricAvailable && (
            <button
              className="auth-switch-btn"
              onClick={() => {
                console.log('🔐 Switching to biometric auth');
                setAuthMethod('biometric');
                setError(null);
              }}
              disabled={isAuthenticating}
            >
              <Icons.Fingerprint size={20} />
              Utiliser {biometricAuth.isBiometricSupported() ? 'Touch ID / Face ID' : 'la biométrie'}
            </button>
          )}

          {authMethod === 'biometric' && (
            <button
              className="auth-switch-btn"
              onClick={() => {
                console.log('🔢 Switching to PIN auth');
                setAuthMethod('pin');
                setError(null);
              }}
              disabled={isAuthenticating}
            >
              <Icons.Hash size={20} />
              Utiliser le code PIN
            </button>
          )}
        </div>

        {/* Hint */}
        <p className="lock-screen-hint">
          Vous avez oublié votre PIN ? Contactez le support ou réinitialisez l'application.
        </p>
      </div>
    </div>
  );
}
